import { HierarchicalTeaseEntry } from "frontend/contentful/schema/blocks";
import { createContentfulComponent } from "frontend/contentful/lib/create-contentful-component";
import { ArticleTeaseProps } from "design-system/components/primitives/article-tease/article-tease";
import { RenderContentfulRichText } from "../utils/render-contentful-rich-text";
import { useSearch } from "frontend/hooks/use-search";
import { TeaseStyle } from "design-system/types/types";
import { HierarchicalTease } from "design-system/components/blocks/hierarchical-tease/hierarchical-tease";
import { createUseSearchProps } from "../utils/use-search.props";
import { createSearchArticleTease } from "frontend/contentful/search/search-article-tease";
import { createCtaLinkProps } from "../primitives/cta-link.props";
import { LegacySearchHit } from "frontend/types";
import { CreateEditAttributesFn } from "frontend/contentful/lib/edit-attributes-factory";
import { getSiteId } from "frontend/lib/get-site-id";
import { usePageProps } from "frontend/hooks/use-page-props";
import { useContentful } from "frontend/hooks/use-contentful";
import { useCuratedTeases } from "../utils/use-curated-teases";

const CuratedContentfulHierarchicalTease = ({
  entry,
  createEditAttributes,
}: {
  entry: HierarchicalTeaseEntry;
  createEditAttributes: CreateEditAttributesFn;
}) => {
  const page = usePageProps();
  const { getEntry } = useContentful();
  const site = getEntry(page.site);
  const siteId = getSiteId(site);

  const articles: Array<ArticleTeaseProps> | undefined = useCuratedTeases(
    entry.fields.teasesCurated || [],
    createSearchArticleTease,
    4,
    siteId,
  );
  if (!articles || articles.length === 0) return null;

  return (
    <HierarchicalTease
      articles={articles}
      feedTeaseStyle={entry.fields.feedTeaseStyle as TeaseStyle}
      header={{
        componentHeader: {
          isSmall: false,
          title: entry.fields.title,
          editAttributes: {
            title: createEditAttributes({ entry, fieldId: "title" }),
          },
        },
      }}
      editAttributes={{
        query: createEditAttributes({
          entry: entry.fields.query,
          fieldId: "query",
        }),
      }}
    />
  );
};

const QueryContentfulHierarchicalTease = ({
  entry,
  createEditAttributes,
}: {
  entry: HierarchicalTeaseEntry;
  createEditAttributes: CreateEditAttributesFn;
}) => {
  const page = usePageProps();
  const { getEntry } = useContentful();
  const site = getEntry(page.site);
  const siteId = getSiteId(site);

  const articles: Array<ArticleTeaseProps> = [];

  const searchProps = createUseSearchProps(entry.fields.query);
  if (searchProps) searchProps.size = 4 + 4;

  const search = useSearch<LegacySearchHit>(searchProps);

  if (Array.isArray(search.data?.hits)) {
    const allTitles: Set<string> = new Set();
    let articleWithImage: ArticleTeaseProps | null = null;

    for (const data of search.data.hits) {
      const props = createSearchArticleTease(data, siteId, { pageProps: page });
      const title = props?.title?.toString();
      const hasImage = !!props?.image?.src;

      if (props && title && !allTitles.has(title)) {
        if (!articleWithImage && hasImage) {
          // if this is the first article with an image, save it
          articleWithImage = props;
          allTitles.add(title);
        } else if (articles.length < 4) {
          // otherwise, add to the list of other articles
          articles.push(props);
          allTitles.add(title);
        }
      }
      if ((articleWithImage ? 1 : 0) + articles.length >= 4) break;
    }
    // if article with image, insert it first
    if (articleWithImage) {
      articles.unshift(articleWithImage);
      // make sure we don't exceed 4 articles in total
      if (articles.length > 4) articles.length = 4;
    }
  }

  if (
    entry.fields.headerSize === "standard" ||
    entry.fields.headerSize === undefined
  ) {
    return (
      <HierarchicalTease
        articles={articles}
        feedTeaseStyle={entry.fields.feedTeaseStyle as TeaseStyle}
        header={{
          componentHeader: {
            isSmall: false,
            title: entry.fields.title,
            editAttributes: {
              title: createEditAttributes({ entry, fieldId: "title" }),
            },
          },
        }}
        editAttributes={{
          query: createEditAttributes({
            entry: entry.fields.query,
            fieldId: "query",
          }),
        }}
      />
    );
  }

  return (
    <HierarchicalTease
      articles={articles}
      feedTeaseStyle={entry.fields.feedTeaseStyle as TeaseStyle}
      header={{
        title: entry.fields.title,
        overline: entry.fields.overline,
        cta: createCtaLinkProps(entry.fields.link),
        subheading: (
          <RenderContentfulRichText document={entry.fields.description} />
        ),
        editAttributes: {
          title: createEditAttributes({ entry, fieldId: "title" }),
          overline: createEditAttributes({ entry, fieldId: "overline" }),
          subheading: createEditAttributes({
            entry,
            fieldId: "description",
          }),
        },
      }}
      editAttributes={{
        query: createEditAttributes({
          entry: entry.fields.query,
          fieldId: "query",
        }),
      }}
    />
  );
};

export const ContentfulHierarchicalTease =
  createContentfulComponent<HierarchicalTeaseEntry>(
    ({ entry, createEditAttributes }) => {
      if (entry.fields.teasesCurated) {
        return (
          <CuratedContentfulHierarchicalTease
            entry={entry}
            createEditAttributes={createEditAttributes}
          />
        );
      }
      if (entry.fields.query) {
        return (
          <QueryContentfulHierarchicalTease
            entry={entry}
            createEditAttributes={createEditAttributes}
          />
        );
      }

      return null;
    },
  );
